import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import { Observer, useLocalStore, useObserver } from "mobx-react";
import { Box, Grid, Paper, ThemeProvider } from "@material-ui/core";
import { store } from "@store";
import {
  BookingEventLog_GetEvents,
  BookingProfile_Get,
  BookingProfile_GetContacts,
  BookingProfile_GetLastCreateBookingKitWfStatus,
  BookingProfile_GetCreateBookingKitWfStatuses,
  BookingScheduleSearch_Get,
  BookingWeekDocumentTypes,
  GetBookingFiles,
  TimeReport_GetBookingInfo,
  TimeReportKit_GetInfo,
  TimereportKitSupportSearch_Get,
  useBookingProfile_GetLastCreateBookingKitWfStatusLazyQuery,
  useBookingKit_RunAutoCreateBookingKitMutation,
  useSetSupportTagToBookingMutation,
  useUploadBookingDocMutation,
  WorkflowStatus
} from "@graphql";
import locale from "@bonliva-crm/locale";
import { Audit } from "@containers";
import { BasicProfileLayout } from "@containers/BasicProfileLayout";
import { ContactSelectorDialog } from "@containers/ContactSelectorDialog";
import { ErrorCard, SpinnerOverlay, Topbar } from "@components";
import { ErrorBoundary } from "@components/error-boundary";
import { BookingProfileStore } from "./BookingProfileStore";
import BookingProfileLayout from "./BookingProfileLayout";
import { TimeReport } from "./TimeReport/TimeReport";
import { BookingGroupSection } from "./BookingGroupSection/BookingGroupSection";
import { BookingWeekCard } from "../BookingWeekCard/BookingWeekCard";
import { BookingFinanceEditor } from "../BookingFinanceEditor/BookingFinanceEditor";
import { BookingProject } from "./BookingProject/BookingProject";
import {
  BookingEditLocation,
  BookingProfileLocation,
  Navigation,
  useNavigation,
  WithNavigation
} from "@navigation";
import { BookingHospitality } from "../BookingHospitality/BookingHospitality";
import { BookingFinanceFormStore } from "../BookingFinanceEditor/BookingFinanceEditorStore";
import BookingEventLog from "@containers/EventLog/BookingEventLog/BookingEventLog";
import { DnDLocalContainer } from "@containers/DnDContainer";
import { DocumentUploader } from "@containers/DocumentUploader/DocumentUploader";
import { useSnackbars } from "@hooks";
import { dateToWeekYear, dateToWeekYear1 } from "@bonliva-crm/common";
import { BookingAlertsQuery } from "@bookings/components/BookingAlerts/BookingAlertsQuery";
import { BookingInvoicingStore } from "@bookings/BookingInvoicing/BookingInvoicingStore";
import { BookingSupportTagForm } from "./BookingSupportTag/BookingSupportTagForm";
import { BookingDocuments } from "./BookingDocuments/BookingDocuments";
import { KitListByBooking } from "./TimeReport/TRKit/kit-list-by-booking";
import { BookingStatus } from "./BookingStatus";
import { CancelBookingButton } from "./cancel-booking-buttons/cancel-booking-button";
import { CancelBookingGroupButton } from "./cancel-booking-buttons/cancel-booking-group-button";
import { BookingCallNumberForm } from "./booking-call-number/booking-call-number-form";
import { bonlivaPipelineTheme, usePolling } from "@bonliva-crm/ui";
import { BookingKitAutomationStatus } from "./booking-kit-automation-status/booking-kit-automation-status";
import { BookingKitWfStatusesHistory } from "./booking-kit-wf-statuses-history/booking-kit-wf-statuses-history";

type Props = {
  id: string;
  selectedTab?: string;
};

const docTypeOptions = [
  BookingWeekDocumentTypes.CallResponse,
  BookingWeekDocumentTypes.UncertifiedTimeReport,
  BookingWeekDocumentTypes.CertifiedTimeReport,
  BookingWeekDocumentTypes.ExpensesReceipts,
  BookingWeekDocumentTypes.InvoiceDocs,
  BookingWeekDocumentTypes.AccommodationTravelInvoices,
  BookingWeekDocumentTypes.SalaryBasis,
  BookingWeekDocumentTypes.BenefitTaxation,
  BookingWeekDocumentTypes.StandingCertificateFromCustomer,
  BookingWeekDocumentTypes.SickPayBasis,
  BookingWeekDocumentTypes.CreditUnderSupplierInvoice,
  BookingWeekDocumentTypes.Other
];

export function BookingProfile(props: Props) {
  const [files, setFiles] = useState<File[]>([]);
  const [isFirstTab, setIsFirstTab] = useState(true);
  const [showBookingKitAutomationStatus, setBookingKitAutomationStatus] = useState(false);
  const snackbars = useSnackbars();

  const [uploadDocs] = useUploadBookingDocMutation();

  const { id } = props;
  const { navigate } = useNavigation();
  const { showSuccess, showError } = useSnackbars();
  const bookingFinanceFormRef = useRef<BookingFinanceFormStore>(null);
  const bookingInvoiceFormRef = useRef<BookingInvoicingStore>(null);

  const localStore = useLocalStore(() => new BookingProfileStore(id));

  useEffect(() => {
    localStore.init(id);
  }, [id]);

  const [runAutoCreateBookingKit] = useBookingKit_RunAutoCreateBookingKitMutation();
  const [fetchLastKitStatus, { data: lastKitStatusData, previousData }] =
    useBookingProfile_GetLastCreateBookingKitWfStatusLazyQuery({
      onCompleted: (data) => {
        if (!data.getLastWorkflowRun) {
          return;
        }

        const prev = previousData?.getLastWorkflowRun;
        const current = data.getLastWorkflowRun;

        const prevStatus = prev?.status;
        const prevResultStatus = prev?.result?.data?.status;

        const currentStatus = current.status;
        const currentResultStatus = current.result?.data?.status;

        const prevSuccess =
          prevStatus === WorkflowStatus.Completed && prevResultStatus === "completed";
        const prevRunning = prevStatus === WorkflowStatus.Running;
        const prevFailed =
          prevStatus === WorkflowStatus.Failed ||
          (prevStatus === WorkflowStatus.Completed && prevResultStatus === "failed");

        const nowSuccess =
          currentStatus === WorkflowStatus.Completed &&
          currentResultStatus === "completed";
        const nowRunning = currentStatus === WorkflowStatus.Running;
        const nowFailed =
          currentStatus === WorkflowStatus.Failed ||
          (currentStatus === WorkflowStatus.Completed &&
            currentResultStatus === "failed");

        if (!prev && !nowSuccess) {
          setBookingKitAutomationStatus(true);
          return;
        }

        if ((prevFailed || prevRunning) && nowSuccess) {
          showSuccess(locale["Kit created!"]);
          setTimeout(() => setBookingKitAutomationStatus(false), 10000);
          return;
        }

        if (prevSuccess && nowRunning) {
          setBookingKitAutomationStatus(true);
          return;
        }

        if ((prevSuccess || prevRunning) && nowFailed) {
          showError(locale["Kit creation failed!"]);
          return;
        }
      }
    });

  usePolling(
    () => {
      fetchLastKitStatus({
        variables: { bookingId: id, workflowType: "createBookingKit" },
        fetchPolicy: "network-only"
      });
    },
    `/bookings/:id/:tabName`,
    { interval: 5000, startImmediate: true, refetchOnPushState: true }
  );

  const handleUpload = async (documentType, files) => {
    for (const file of files) {
      await uploadDocs({
        variables: {
          id,
          file,
          documentType
        },
        refetchQueries: [
          BookingEventLog_GetEvents.OperationName,
          GetBookingFiles.OperationName,
          TimeReportKit_GetInfo.OperationName
        ]
      }).then(() => showSuccess(`Uploaded`));
    }
  };

  const handleRunAutoCreate = () => {
    runAutoCreateBookingKit({
      variables: { bookingId: id },
      refetchQueries: [
        TimeReportKit_GetInfo.OperationName,
        TimeReport_GetBookingInfo.OperationName,
        BookingProfile_GetLastCreateBookingKitWfStatus.OperationName,
        BookingProfile_GetCreateBookingKitWfStatuses.OperationName
      ]
    })
      .then(() => {
        snackbars.showInfo(locale["Automation process started. Kit is being created"]);
      })
      .catch((err) => {
        snackbars.showError(`Error: ${err}`);
        console.error(err);
      });
  };

  const [submitTag, { loading: loadingTags }] = useSetSupportTagToBookingMutation({
    refetchQueries: [
      BookingProfile_Get.OperationName,
      TimereportKitSupportSearch_Get.OperationName,
      BookingScheduleSearch_Get.OperationName,
      TimeReport_GetBookingInfo.OperationName
    ]
  });

  const handleSubmitTag = async (bookingId: string, comment: string, tags: string[]) => {
    const res = await submitTag({
      variables: {
        bookingId,
        comment,
        tags
      }
    });

    return res.data.setSupportTagToBooking;
  };

  const request = () => {
    store()
      .upload.requestFileUpload({ multiple: true })
      .then((files) => setFiles(files));
  };

  return useObserver(() => {
    const { bookingData: data } = localStore;
    const { selectedTab: tab } = props;

    if (localStore.fsm.is("loading")) {
      return <SpinnerOverlay transparent show />;
    }

    if (localStore.fsm.is("error")) {
      return (
        <BasicProfileLayout>
          <ErrorCard
            centered
            message={localStore.error}
            title={locale.ErrorOccurred}
            style={{ marginTop: 120 }}
          />
        </BasicProfileLayout>
      );
    }

    return (
      <ErrorBoundary>
        <DnDLocalContainer
          multiple
          onDrop={(f) => setFiles(f)}
          noClick
          disabled={tab === "hospitality" || tab === "finance" || tab === "timereport"}
        >
          <DocumentUploader
            documentTypes={docTypeOptions.toLabelValue(
              (v) => locale.bookingWeekDocumentTypes[v]
            )}
            files={files}
            onUpload={(docType) => handleUpload(docType, files)}
            onClose={() => setFiles([])}
          />
          <BookingProfileLayout
            id={id}
            currentTab={tab}
            tabOnChange={(tab) => {
              setIsFirstTab(false);
              navigate(new BookingProfileLocation(id, tab));
            }}
            financeTab={() => {
              return (
                <BookingFinanceEditor
                  bookingId={id}
                  bookingData={data}
                  ref={bookingFinanceFormRef}
                  invoiceFormRef={bookingInvoiceFormRef}
                  forceLoad={!isFirstTab}
                />
              );
            }}
            filesTab={() => <BookingDocuments id={id} onUpload={request} />}
            timeReportKitTab={() => (
              <ThemeProvider theme={bonlivaPipelineTheme}>
                <KitListByBooking
                  bookingId={id}
                  status={data.timeReportStatus}
                  week={dateToWeekYear1(data.Week).week}
                  legalPersonType={data.ConsultantSnapshot.LegalPersonType}
                />
              </ThemeProvider>
            )}
            bookingKitCreateWfStatus={() =>
              showBookingKitAutomationStatus ? (
                <BookingKitAutomationStatus data={lastKitStatusData ?? null} />
              ) : null
            }
            bookingKitAutomationTab={() => (
              <BookingKitWfStatusesHistory
                tabActive={tab === "kitAutomation"}
                bookingId={id}
                handleRunAutoCreate={handleRunAutoCreate}
                autoCreateBookingKitDisabled={
                  lastKitStatusData?.getLastWorkflowRun?.status === WorkflowStatus.Running
                }
              />
            )}
            eventsTab={() => <BookingEventLog id={id} />}
            alerts={() => <BookingAlertsQuery data={localStore.bookingData} />}
            timeReportTab={() => (
              <TimeReport
                bookingId={id}
                consultantId={data.Consultant._id}
                consultantName={data.Consultant.Name}
                week={dateToWeekYear1(data.Week).week}
                year={dateToWeekYear1(data.Week).year}
                bookingProfileStore={localStore}
                handleRunAutoCreate={handleRunAutoCreate}
                autoCreateBookingKitDisabled={
                  lastKitStatusData?.getLastWorkflowRun?.status === WorkflowStatus.Running
                }
              />
            )}
            hospitalityTab={() => (
              <BookingHospitality
                bookingId={id}
                bookingGroupId={localStore.bookingData.BookingGroup._id}
                consultantName={localStore.bookingData.Consultant?.Name}
                week={dateToWeekYear(data.Week)}
              />
            )}
            main={
              <WithNavigation>
                {(navigation: Navigation) => (
                  <>
                    <ErrorBoundary>
                      <BookingWeekCard
                        item={data}
                        onEdit={() => navigation.navigate(new BookingEditLocation(id))}
                      />
                    </ErrorBoundary>
                    <Grid container spacing={1} alignItems={"stretch"}>
                      <Grid item xs={6}>
                        <BookingProject bookingId={id} data={data} clearable={false} />
                        <BookingCallNumberForm
                          bookingId={id}
                          hospitalId={data?.BookingGroup?.hospital?._id}
                          callNumber={data?.callNumber}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Paper style={{ padding: 16, height: "100%" }}>
                          <BookingStatus
                            key={`${data.IsConsultantPayed}-${data.IsCustomerBilled}`}
                            data={data}
                          />

                          <Box mt={1}>
                            <BookingSupportTagForm
                              key={
                                data.bookingSupportTagStatus
                                  ? data?.bookingSupportTagStatus
                                      ?.map((x) => x.tag)
                                      .join()
                                  : "[]"
                              } // hack to upd data on tag change
                              initialData={data}
                              bookingOrKit={"booking"}
                              onSubmit={handleSubmitTag}
                              loading={loadingTags}
                            />
                          </Box>
                        </Paper>
                      </Grid>
                    </Grid>

                    <ErrorBoundary>
                      <BookingGroupSection
                        bookingGroupId={data.BookingGroup._id}
                        week={data.Week}
                        onWeekChange={(v) => {
                          navigation.navigate(new BookingProfileLocation(v));
                        }}
                      />
                    </ErrorBoundary>
                  </>
                )}
              </WithNavigation>
            }
            topbar={
              <Observer>
                {() => (
                  <Topbar>
                    <Box ml="auto">
                      <CancelBookingButton bookingId={id} />
                      <CancelBookingGroupButton bookingId={id} />
                    </Box>
                  </Topbar>
                )}
              </Observer>
            }
            footer={
              <Audit
                createdOn={data.createdOn}
                createdBy={data.createdBy?.Name}
                updatedOn={data.updatedOn}
                updatedBy={data.updatedBy?.Name}
                deletedOn={data.deletedOn}
                deletedBy={data.deletedBy?.Name}
              />
            }
            week={moment.utc(data.Week).format("GGGG-WW")}
          />

          <ContactSelectorDialog
            allowEmpty
            open={localStore.fsm.is("contactSelector")}
            onClose={localStore.toInitial}
            onFetch={() =>
              BookingProfile_GetContacts.func(
                { id },
                { fetchPolicy: "network-only" }
              ).then((x) => x.data.getBookingCustomerContacts)
            }
            onSubmit={(items) => localStore.toSendTimereport(items)}
          />
        </DnDLocalContainer>
      </ErrorBoundary>
    );
  });
}
